/**
 * The console's Moderation tab: who is currently banned, and the form that puts somebody there.
 *
 * Drawn for a moderator as well as an admin, unlike the Users and Room roster tabs — this is the
 * surface the moderator role exists for. As everywhere else in this console that is a
 * convenience: `admin.moderation` is `moderatorProcedure` on the server, and the rules about who
 * may be banned are enforced there. What this panel does is show the refusal when one comes back,
 * because "a moderator cannot ban another moderator" is a sentence an operator needs to read
 * rather than a greyed-out button they have to guess at.
 *
 * The search is a lookup, not a roster: `admin.moderation.findUsers` answers at most ten rows for
 * a name or an address somebody already has in mind. Paging through every account stays on the
 * admin-only Users tab.
 *
 * Expiry is drawn from `expiresAt` and nothing else — the server never sends "is expired",
 * because a ban ends when that instant passes and the list simply stops carrying it.
 *
 * The **two approval queues** are the tab's other half, and they are here rather than on screens
 * of their own because it is the same job and the same role: a moderator decides who may play,
 * what may be said and what may be seen. They are shaped differently from the bans list on
 * purpose — oldest first and paged, where it is neither — because a queue is *work* with an
 * expected backlog rather than a set of people currently kept out. See `listPendingSayings`.
 *
 * The image queue is the saying queue with a picture where the sentence is, down to the guarded
 * UPDATE behind each verdict. The bytes are loaded straight from the bucket, exactly as every
 * player's browser would load them — which is the point: a moderator has to see what a room
 * would have seen.
 */

import { useState } from 'react';
import type {
  AdminBanRow,
  AdminImageRow,
  AdminModerationUserRow,
  AdminSayingRow,
} from '../../../../server/src/trpc/routers/admin-moderation.js';
import { type BanTarget, BanDialog } from '@/components/admin/ban-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { formatBytes } from '@/lib/image-upload';
import { trpc } from '@/lib/trpc';

export function ModerationPanel() {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [target, setTarget] = useState<BanTarget | null>(null);
  const [lifting, setLifting] = useState<AdminBanRow | null>(null);

  const bans = trpc.admin.moderation.listBans.useQuery();
  const found = trpc.admin.moderation.findUsers.useQuery(
    { search },
    // Nothing typed yet is nothing to look up; the list below says so rather than querying.
    { enabled: search !== '' },
  );

  const queue = trpc.admin.moderation.listPendingSayings.useQuery();
  const imageQueue = trpc.admin.moderation.listPendingImages.useQuery();

  const refresh = async () => {
    await Promise.all([
      utils.admin.moderation.listBans.invalidate(),
      utils.admin.moderation.findUsers.invalidate(),
    ]);
  };

  const reviewSaying = trpc.admin.moderation.reviewSaying.useMutation({
    onSuccess: async () => {
      await utils.admin.moderation.listPendingSayings.invalidate();
    },
  });

  const reviewImage = trpc.admin.moderation.reviewImage.useMutation({
    onSuccess: async () => {
      await utils.admin.moderation.listPendingImages.invalidate();
    },
  });

  const banUser = trpc.admin.moderation.banUser.useMutation({
    onSuccess: async () => {
      setTarget(null);
      await refresh();
    },
  });
  const liftBan = trpc.admin.moderation.liftBan.useMutation({
    onSuccess: async () => {
      setLifting(null);
      await refresh();
    },
  });

  return (
    <section className="space-y-4">
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Ban an account
        </h2>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(draft.trim());
          }}
        >
          <Input
            type="search"
            className="h-9 w-48 sm:w-64"
            placeholder="Email or display name"
            aria-label="Find an account to ban"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="outline" size="sm">
            Find
          </Button>
          {search !== '' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft('');
                setSearch('');
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {found.error && (
          <p className="text-sm text-destructive" role="alert">
            {found.error.message}
          </p>
        )}

        {search !== '' && (
          <Card>
            <CardContent className="p-4">
              {found.isLoading ? (
                <p className="py-2 text-center text-sm text-muted-foreground">Searching…</p>
              ) : (found.data ?? []).length === 0 ? (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  No account matches “{search}”.
                </p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {(found.data ?? []).map((row) => (
                    <SearchResult key={row.id} row={row} onBan={() => setTarget(row)} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Active bans
        </h2>

        {bans.error && (
          <p className="text-sm text-destructive" role="alert">
            {bans.error.message}
          </p>
        )}
        {liftBan.error && (
          <p className="text-sm text-destructive" role="alert">
            {liftBan.error.message}
          </p>
        )}

        <Card>
          <CardContent className="p-4">
            {bans.isLoading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
            ) : (bans.data ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nobody is banned right now.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Account</th>
                      <th className="py-2 pr-3 font-medium">Reason</th>
                      <th className="py-2 pr-3 font-medium">Expires</th>
                      <th className="py-2 pr-3 font-medium">Issued by</th>
                      <th className="py-2 font-medium">Lift</th>
                    </tr>
                  </thead>
                  <tbody data-testid="ban-rows">
                    {(bans.data ?? []).map((row) => (
                      <BanRowCells key={row.id} row={row} onLift={() => setLifting(row)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <SayingQueue
        rows={queue.data?.rows ?? []}
        total={queue.data?.total ?? 0}
        isLoading={queue.isLoading}
        error={queue.error?.message ?? reviewSaying.error?.message ?? null}
        pending={reviewSaying.isPending}
        onReview={(submissionId, verdict) => reviewSaying.mutate({ submissionId, verdict })}
      />

      <ImageQueue
        rows={imageQueue.data?.rows ?? []}
        total={imageQueue.data?.total ?? 0}
        isLoading={imageQueue.isLoading}
        error={imageQueue.error?.message ?? reviewImage.error?.message ?? null}
        pending={reviewImage.isPending}
        onReview={(imageId, verdict) => reviewImage.mutate({ imageId, verdict })}
      />

      {target && (
        <BanDialog
          target={target}
          serverError={banUser.error?.message ?? null}
          pending={banUser.isPending}
          onSubmit={({ duration, reason }) =>
            banUser.mutate({ userId: target.id, duration, reason })
          }
          onClose={() => setTarget(null)}
        />
      )}

      {lifting && (
        <Dialog
          open
          title="Lift this ban?"
          description={`${lifting.displayName} will be able to log in and join a room again straight away. The ban stays in the history with your name on it.`}
          onClose={() => setLifting(null)}
        >
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setLifting(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={liftBan.isPending}
              onClick={() => liftBan.mutate({ banId: lifting.id })}
            >
              {liftBan.isPending ? 'Lifting…' : 'Lift ban'}
            </Button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/**
 * Pending custom sayings, oldest first, with the two verdicts beside each one.
 *
 * A moderator acts without leaving the tab: `reviewSaying` is an UPDATE guarded on
 * `status = 'pending'`, so two people pressing the button together do not overwrite each other
 * and a decided row cannot be re-decided — both come back as the same "there is nothing to decide"
 * message, whose answer is the refresh this query does on success.
 *
 * The submitter rides each row because a moderator reading a sentence needs to know who wrote it,
 * and whether the account is a **guest** — a guest is one refresh from a different row, so the
 * same words from one are worth less patience than from a registered account.
 */
function SayingQueue({
  rows,
  total,
  isLoading,
  error,
  pending,
  onReview,
}: {
  rows: readonly AdminSayingRow[];
  total: number;
  isLoading: boolean;
  error: string | null;
  pending: boolean;
  onReview: (submissionId: string, verdict: 'approved' | 'rejected') => void;
}) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Sayings waiting for approval{total > 0 ? ` (${total})` : ''}
      </h2>

      {error !== null && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nothing is waiting for a verdict.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Saying</th>
                    <th className="py-2 pr-3 font-medium">Submitted by</th>
                    <th className="py-2 pr-3 font-medium">Sent</th>
                    <th className="py-2 font-medium">Verdict</th>
                  </tr>
                </thead>
                <tbody data-testid="saying-queue-rows">
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      <td className="max-w-72 py-2 pr-3 font-medium">{row.text}</td>
                      <td className="py-2 pr-3">
                        <div>{row.displayName}</div>
                        <div className="break-all text-xs text-muted-foreground">
                          {row.email ?? (row.isGuest ? 'Guest' : 'No email')}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {formatInstant(row.createdAt)}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            data-testid={`approve-saying-${row.id}`}
                            disabled={pending}
                            onClick={() => onReview(row.id, 'approved')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`reject-saying-${row.id}`}
                            disabled={pending}
                            onClick={() => onReview(row.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Uploaded images waiting for a verdict, oldest first, with the two verdicts beside each one.
 *
 * The picture is loaded from the bucket rather than from anything this server holds — a
 * moderator has to be looking at what a room would have shown. An `approved` image starts
 * travelling on that player's *next* ticket; a `rejected` one keeps its row, and its bytes are
 * deleted from the bucket after the verdict is written down.
 *
 * A guest cannot upload at all, so every submitter here is a registered account; the field is
 * drawn beside the saying queue's for the reader's sake rather than because it can differ.
 */
function ImageQueue({
  rows,
  total,
  isLoading,
  error,
  pending,
  onReview,
}: {
  rows: readonly AdminImageRow[];
  total: number;
  isLoading: boolean;
  error: string | null;
  pending: boolean;
  onReview: (imageId: string, verdict: 'approved' | 'rejected') => void;
}) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Images waiting for approval{total > 0 ? ` (${total})` : ''}
      </h2>

      {error !== null && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No images are waiting for a verdict.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="image-queue-rows">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center gap-4 border-b border-border/60 pb-3 last:border-0 last:pb-0"
                >
                  <img
                    src={row.url}
                    alt={`${row.kind} uploaded by ${row.displayName}`}
                    className="size-24 shrink-0 rounded-lg border border-border object-cover"
                    width={96}
                    height={96}
                  />
                  <div className="min-w-40 flex-1 text-sm">
                    <div className="font-medium">{row.displayName}</div>
                    <div className="break-all text-xs text-muted-foreground">
                      {row.email ?? (row.isGuest ? 'Guest' : 'No email')}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {row.kind === 'avatar' ? 'Profile picture' : 'Circle photo'} · {row.width}×
                      {row.height} · {formatBytes(row.byteSize)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatInstant(row.createdAt)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      data-testid={`approve-image-${row.id}`}
                      disabled={pending}
                      onClick={() => onReview(row.id, 'approved')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`reject-image-${row.id}`}
                      disabled={pending}
                      onClick={() => onReview(row.id, 'rejected')}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SearchResult({ row, onBan }: { row: AdminModerationUserRow; onBan: () => void }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2">
      <div>
        <div className="font-medium">{row.displayName}</div>
        <div className="break-all text-xs text-muted-foreground">
          {row.email ?? 'Guest'} · {row.role}
        </div>
      </div>
      {row.activeBanId === null ? (
        <Button variant="destructive" size="sm" onClick={onBan}>
          Ban
        </Button>
      ) : (
        <span className="text-xs text-muted-foreground">Already banned</span>
      )}
    </li>
  );
}

function BanRowCells({ row, onLift }: { row: AdminBanRow; onLift: () => void }) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="py-2 pr-3">
        <div className="font-medium">{row.displayName}</div>
        <div className="break-all text-xs text-muted-foreground">
          {row.email ?? (row.isGuest ? 'Guest' : 'No email')}
        </div>
      </td>
      <td className="max-w-64 py-2 pr-3">{row.reason}</td>
      <td className="py-2 pr-3 text-muted-foreground">
        {row.expiresAt === null ? 'Never (permanent)' : formatInstant(row.expiresAt)}
        <div className="text-xs">{row.durationLabel}</div>
      </td>
      <td className="py-2 pr-3 text-muted-foreground">{row.issuedByName ?? 'Deleted account'}</td>
      <td className="py-2">
        <Button variant="outline" size="sm" onClick={onLift}>
          Lift
        </Button>
      </td>
    </tr>
  );
}

/** The reader's own timezone; the server sends an ISO instant precisely so it can be. */
function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString();
}
